import { Check } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import StatCounter from "@/components/StatCounter";
import { PageHero, CTABand, SectionTitle } from "@/components/blocks";
import { IMAGES } from "@/data/content";
import { FounderSection } from "@/components/FounderSection";

const VALUES = [
  { t: "Passion automobile", d: "Une équipe de passionnés, mécaniciens et ingénieurs moteur, animés par la performance." },
  { t: "Expertise technique", d: "Des cartographies développées sur mesure, jamais des fichiers génériques." },
  { t: "Le banc, notre référence", d: "Chaque préparation est mesurée et validée au banc de puissance." },
  { t: "Un réseau national", d: "Une méthodologie commune déployée dans chacun de nos centres en France." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo title="Nous connaître | L'histoire du réseau Horsepower Garage"
        description="Découvrez Horsepower Garage : histoire, expertise, philosophie et développement du réseau national de reprogrammation moteur, depuis la maison mère de Béziers."
        canonical="https://www.horsepowergarage.fr/nous-connaitre" />
      <PageHero eyebrow="Nous connaître" title="La passion de la performance"
        subtitle="De la maison mère de Béziers à un réseau national : l'histoire d'une expertise partagée."
        image="/photos/hero-beziers.jpg" tall />

      <FounderSection />

      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-20">
        <Reveal>
          <p className="text-neutral-300 text-lg leading-relaxed">
            Horsepower Garage est né d'une conviction : chaque véhicule possède un potentiel inexploité. Depuis notre
            centre historique de Béziers, notre maison mère, nous développons des solutions de reprogrammation moteur sur
            mesure, validées au banc de puissance.
          </p>
          <p className="text-neutral-400 mt-6 leading-relaxed">
            Ce savoir-faire, nous l'avons structuré et déployé à travers un réseau national de centres : Montpellier,
            Perpignan, Marseille, Angers et Metz. Une même méthodologie, une même exigence, partout en France. Reprogrammation
            Stage 1, 2 et 3, conversion E85 FlexFuel, diagnostic et préparation automobile : nos ingénieurs moteur mettent
            la technologie au service de la performance et de la fiabilité.
          </p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[{ n: 6, s: "", l: "Centres" }, { n: 10, s: "+", l: "Ans d'expertise" }, { n: 5000, s: "+", l: "Véhicules" }, { n: 100, s: "%", l: "Sur mesure" }].map((x, i) => (
            <Reveal key={x.l} delay={i * 0.08}>
              <div className="hp-card p-6 text-center">
                <div className="font-stat font-black text-4xl sm:text-5xl text-hp-yellow"><StatCounter to={x.n} suffix={x.s} /></div>
                <p className="text-neutral-400 text-xs uppercase tracking-widest mt-2">{x.l}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <SectionTitle eyebrow="Nos valeurs" title="Ce qui nous anime" />
        <div className="grid sm:grid-cols-2 gap-5 mt-10">
          {VALUES.map((v, i) => (
            <Reveal key={v.t} delay={(i % 2) * 0.08}>
              <div className="hp-card p-7 flex gap-4">
                <Check className="text-hp-yellow shrink-0 mt-1" size={22} />
                <div><h3 className="font-display font-bold uppercase tracking-tight text-lg">{v.t}</h3><p className="text-neutral-400 mt-1.5 text-sm leading-relaxed">{v.d}</p></div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {IMAGES.gallery.map((img, i) => (
            <Reveal key={i} delay={(i % 3) * 0.06}>
              <div className="relative aspect-[4/3] overflow-hidden hp-card group">
                <img src={img} alt="Atelier Horsepower" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
              </div>
            </Reveal>
          ))}
        </div>
        <p className="text-xs text-neutral-600 mt-4 text-center">Visuels d'illustration. Vos photos réelles d'équipes, centres et bancs peuvent être ajoutées depuis l'administration.</p>
      </section>
      <CTABand />
    </div>
  );
}
